/**
 * Qinggong time trials (ด่านจับเวลาวิชาตัวเบา) on the new Yanyu (docs/systems/FUN_P1.md section 3). A trial is a list of rings
 * (radius 3 m; a ring with a height `h` also wants the player within 3 m of it, in metres above the sea) taken in
 * order: the first is the start stone, the last the finish. Par times assume the run pace of data/qinggong.ts
 * `QG_RUN_BASE` (ขั้น 1 of the arts named in `needs`). Rules: systems/trials.ts. Data only: add a trial here.
 */
import type { ZoneId } from './zones';

export interface TrialRing {
  readonly x: number;
  readonly y: number;
  /** Height above the sea (m), within `TRIAL.band`; left out: any height. */
  readonly h?: number;
}

export type TrialMedal = 'bronze' | 'silver' | 'gold';

export interface TrialDef {
  readonly id: string;
  readonly name: string;
  readonly zone: ZoneId;
  readonly rings: readonly TrialRing[];
  /** Qinggong arts (id → least level) the route is made for; a run without them is refused at the start stone. */
  readonly needs: Readonly<Record<string, number>>;
  /** Seconds for each medal (the run's time at most this). */
  readonly medals: Readonly<Record<TrialMedal, number>>;
}

export const TRIAL = {
  /** A ring's radius (m). */
  radius: 3,
  /** A ring's height band (m). */
  band: 3,
  /** How near the start stone the player must stand to begin (m). */
  startReach: 8,
} as const;

export const TRIALS: readonly TrialDef[] = [
  {
    id: 't.bridges.land',
    name: 'วิ่งรอบสะพานลาน (ทางบก)',
    zone: 'yanyu2',
    // West end of the plaza bridge, east end, the yard gate, the duelling ground gate, back over the bridge.
    rings: [
      { x: 362, y: 300 },
      { x: 420, y: 300 },
      { x: 416, y: 287 },
      { x: 469, y: 297 },
      { x: 420, y: 300 },
      { x: 362, y: 306 },
    ],
    needs: {},
    medals: { gold: 40, silver: 55, bronze: 130 },
  },
  {
    id: 't.bridges',
    name: 'วิ่งรอบสะพานลาน',
    zone: 'yanyu2',
    rings: [
      { x: 362, y: 300 },
      { x: 420, y: 300 },
      { x: 430, y: 376 },
      { x: 416, y: 376 },
      { x: 402, y: 394 },
      { x: 366, y: 376 },
      { x: 362, y: 306 },
    ],
    needs: { 'thousand-li': 1, 'water-skim': 1 },
    medals: { gold: 55, silver: 70, bronze: 90 },
  },
  {
    id: 't.river',
    name: 'เหยียบสายน้ำสู่ทะเล',
    zone: 'yanyu2',
    rings: [
      { x: 372, y: 14 },
      { x: 388, y: 150, h: 0 },
      { x: 398, y: 220 },
      { x: 391, y: 324 },
      { x: 396, y: 340 },
      { x: 402, y: 412 },
      { x: 404, y: 500 },
      { x: 330, y: 556 },
      { x: 329, y: 590 },
    ],
    needs: { 'thousand-li': 1, 'water-skim': 1 },
    medals: { gold: 100, silver: 125, bronze: 160 },
  },
  {
    id: 't.crags',
    name: 'เหินผาสู่ลาน',
    zone: 'yanyu2',
    rings: [
      { x: 538, y: 66, h: 100 },
      { x: 478, y: 164, h: 60 },
      { x: 523, y: 164, h: 88 },
      { x: 570, y: 164, h: 80 },
      { x: 490, y: 300, h: 2 },
    ],
    needs: { 'feather-fall': 1, 'sky-step': 1 },
    medals: { gold: 65, silver: 80, bronze: 100 },
  },
];

export const trialById = (id: string): TrialDef | undefined => TRIALS.find((t) => t.id === id);

/** Pay for the first clear of each medal (a higher medal pays the lower ones not yet taken). */
export const TRIAL_REWARDS: Readonly<Record<TrialMedal, { readonly gold: number; readonly cultivation: number }>> = {
  bronze: { gold: 40, cultivation: 40 },
  silver: { gold: 70, cultivation: 60 },
  gold: { gold: 120, cultivation: 100 },
};

/** The best medal a time earns on a trial, or null. */
export function medalFor(t: TrialDef, seconds: number): TrialMedal | null {
  if (seconds <= t.medals.gold) return 'gold';
  if (seconds <= t.medals.silver) return 'silver';
  if (seconds <= t.medals.bronze) return 'bronze';
  return null;
}
